import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar, 
  TrendingUp, 
  Users, 
  Activity,
  Bell,
  RefreshCw,
  Home,
  ChevronRight,
  BarChart3,
  Clock,
  Sparkles,
  ArrowRight,
  Plus,
  Zap,
  Target,
  Star
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import PageContainer from '@/components/common/PageContainer';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

// Import dashboard widgets
import UpcomingSessionsWidget from '@/components/dashboard/UpcomingSessionsWidget';
import PendingCheckoutsWidget from '@/components/dashboard/PendingCheckoutsWidget';
import NewSubscriptionsWidget from '@/components/dashboard/NewSubscriptionsWidget';
import ExpiringSubscriptionsWidget from '@/components/dashboard/ExpiringSubscriptionsWidget';
import ExpiredSubscriptionsWidget from '@/components/dashboard/ExpiredSubscriptionsWidget';

// Import data service
import { fetchDashboardStats, DashboardStats } from '@/services/dashboard';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [userProfile, setUserProfile] = useState<any>(null);
  const today = new Date();

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await fetchDashboardStats();
      setDashboardData(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar os dados do dashboard. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('first_name')
            .eq('id', user.id)
            .single();
          
          if (!error && data) {
            setUserProfile(data);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };
    
    fetchUserProfile();
  }, [user]);

  const handleRefresh = () => {
    loadDashboardData();
    toast({
      title: "Dados atualizados",
      description: "Dashboard atualizado com sucesso.",
    });
  };

  // Show loading screen during initial load
  if (loading && !dashboardData) {
    return <LoadingScreen message="Carregando dashboard" size="x-small" />;
  }

  // Stats cards data
  const getStatsCards = () => {
    if (!dashboardData) return [];

    return [
      {
        title: "Próximas Formações",
        value: dashboardData.upcomingSessions.length.toString(),
        icon: Calendar,
        description: "Formações agendadas",
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        onClick: () => navigate('/classes')
      },
      {
        title: "Check-outs Pendentes",
        value: dashboardData.pendingCheckouts.length.toString(),
        icon: Clock,
        description: "Necessitam check-out",
        color: "text-orange-600",
        bgColor: "bg-orange-50",
        onClick: () => navigate('/classes')
      },
      {
        title: "Novas Assinaturas",
        value: dashboardData.newSubscriptions.length.toString(),
        icon: TrendingUp,
        description: "Últimos 7 dias",
        color: "text-green-600",
        bgColor: "bg-green-50",
        onClick: () => navigate('/subscriptions')
      },
      {
        title: "Vencendo em 60 dias",
        value: dashboardData.subscriptionsExpiringSoon.length.toString(),
        icon: Activity,
        description: "Precisam atenção",
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        onClick: () => navigate('/subscriptions')
      }
    ];
  };

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Página Inicial</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Header with Background Image */}
        <div 
          className="relative overflow-hidden w-full" 
          style={{ 
            backgroundColor: '#1F1746',
            backgroundImage: 'url(/elemento-01.svg)',
            backgroundSize: '200%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/10"></div>
          
          <div className="relative w-full max-w-7xl mx-auto px-6 py-8 pb-12">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-extrabold text-white">
                      Olá {userProfile?.first_name || user?.user_metadata?.first_name || 'Usuário'} 👋
                    </h1>
                    <p className="text-blue-100 text-base">
                      Acompanhe suas métricas e gerencie suas operações em tempo real
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Badge 
                  className="bg-white/20 text-white border-white/30 backdrop-blur-sm px-3 py-1"
                >
                  <Activity className="h-3 w-3 mr-1" />
                  Última atualização: {format(lastUpdated, 'HH:mm', { locale: ptBR })}
                </Badge>
                {import.meta.env.VITE_APP_ENV === 'staging' && (
                  <Badge 
                    className="bg-yellow-500 text-yellow-900 border-yellow-600 backdrop-blur-sm px-3 py-1 font-bold"
                  >
                    Amb. Homologação
                  </Badge>
                )}
                <Button
                  variant="secondary"
                  size="default"
                  onClick={handleRefresh}
                  disabled={loading}
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-6 relative z-10 space-y-8">
          {/* Enhanced Stats Cards with Modern Design */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {getStatsCards().map((stat, index) => {
              const cardIndex = index % 4;
              const gradientMap = [
                'from-blue-50 to-white',
                'from-orange-50 to-white', 
                'from-green-50 to-white',
                'from-yellow-50 to-white'
              ];
              const circleColorMap = [
                'from-blue-500/10 to-transparent',
                'from-orange-500/10 to-transparent',
                'from-green-500/10 to-transparent', 
                'from-yellow-500/10 to-transparent'
              ];
              const iconBgMap = [
                'bg-blue-100',
                'bg-orange-100',
                'bg-green-100',
                'bg-yellow-100'
              ];
              const textColorMap = [
                'text-blue-600',
                'text-orange-600', 
                'text-green-600',
                'text-yellow-600'
              ];

              return (
                <Card 
                  key={index} 
                  className={`relative overflow-hidden border shadow-sm bg-gradient-to-br ${gradientMap[cardIndex]} cursor-pointer transition-all duration-300 transform hover:-translate-y-1`}
                  onClick={stat.onClick}
                >
                  <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${circleColorMap[cardIndex]} rounded-full transform translate-x-10 -translate-y-10`} />
                  <CardContent className="p-6 relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 ${iconBgMap[cardIndex]} rounded-lg`}>
                          <stat.icon className={`h-5 w-5 ${textColorMap[cardIndex]}`} />
                        </div>
                        <span className="text-sm font-extrabold text-gray-600">{stat.title}</span>
                      </div>
                      <div className="text-3xl font-extrabold text-gray-900">
                        {stat.value}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {stat.description}
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowRight className={`h-4 w-4 ${textColorMap[cardIndex]} group-hover:translate-x-1 transition-transform`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Featured Upcoming Sessions Section */}
          <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-lg">
                    <Calendar className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Próximas Formações</CardTitle>
                    <CardDescription className="text-xs">
                      Formações agendadas que precisam da sua atenção
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    {dashboardData?.upcomingSessions?.length || 0} sessões
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/classes')}
                    className="h-6 px-2 text-xs font-extrabold text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    Ver todas
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-[140px]">
              <UpcomingSessionsWidget 
                sessions={dashboardData?.upcomingSessions || []}
                loading={loading}
              />
            </CardContent>
          </Card>

          {/* Main Dashboard Grid with Enhanced Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Checkouts */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-orange-100 rounded-lg">
                      <Clock className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Check-outs Pendentes</CardTitle>
                      <CardDescription className="text-xs">
                        Sessões que necessitam finalização
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                      {dashboardData?.pendingCheckouts?.length || 0} pendentes
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/classes')}
                      className="h-6 px-2 text-xs font-extrabold text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    >
                      Ver todas
                      <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="h-[350px]">
                <PendingCheckoutsWidget 
                  checkouts={dashboardData?.pendingCheckouts || []}
                  loading={loading}
                />
              </CardContent>
            </Card>

            {/* New Subscriptions */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-100 rounded-lg">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Novas Assinaturas</CardTitle>
                      <CardDescription className="text-xs">
                        Assinaturas criadas nos últimos 7 dias
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200 text-xs">
                      {dashboardData?.newSubscriptions?.length || 0} novas
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/subscriptions')}
                      className="h-6 px-2 text-xs font-extrabold text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      Ver todas
                      <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="h-[350px]">
                <NewSubscriptionsWidget 
                  subscriptions={dashboardData?.newSubscriptions || []}
                  loading={loading}
                />
              </CardContent>
            </Card>
          </div>

          {/* Subscription Management Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Expiring Subscriptions */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-yellow-100 rounded-lg">
                      <Activity className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Vencendo em 60 dias</CardTitle>
                      <CardDescription className="text-xs">
                        Assinaturas que precisam de atenção
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">
                      {dashboardData?.subscriptionsExpiringSoon?.length || 0} vencendo
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/subscriptions')}
                      className="h-6 px-2 text-xs font-extrabold text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                    >
                      Ver todas
                      <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="h-[400px]">
                <ExpiringSubscriptionsWidget 
                  subscriptions={dashboardData?.subscriptionsExpiringSoon || []}
                  loading={loading}
                />
              </CardContent>
            </Card>

            {/* Expired Subscriptions */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-red-100 rounded-lg">
                      <Target className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Assinaturas Vencidas</CardTitle>
                      <CardDescription className="text-xs">
                        Requerem ação imediata
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive" className="bg-red-50 text-red-700 border-red-200 text-xs">
                      {dashboardData?.expiredSubscriptions?.length || 0} vencidas
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/subscriptions')}
                      className="h-6 px-2 text-xs font-extrabold text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Ver todas
                      <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="h-[400px]">
                <ExpiredSubscriptionsWidget 
                  subscriptions={dashboardData?.expiredSubscriptions || []}
                  loading={loading}
                />
              </CardContent>
            </Card>
          </div>

          {/* Enhanced Quick Actions Section */}
          <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-lg">
                    <Zap className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Ações Rápidas</CardTitle>
                    <CardDescription className="text-xs">
                      Acesse rapidamente as funcionalidades mais usadas
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                  <Star className="h-3 w-3 mr-1" />
                  Favoritos
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card
                  className="group cursor-pointer border shadow-sm bg-gradient-to-br from-blue-50 to-white transition-all duration-300 transform hover:-translate-y-1"
                  onClick={() => navigate('/class-wizard')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                        <Calendar className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-gray-900">Nova Classe</h3>
                        <p className="text-xs text-gray-500 mt-1">Criar nova turma</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="group cursor-pointer border shadow-sm bg-gradient-to-br from-green-50 to-white transition-all duration-300 transform hover:-translate-y-1"
                  onClick={() => navigate('/subscription-wizard')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-3 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors">
                        <TrendingUp className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-gray-900">Nova Assinatura</h3>
                        <p className="text-xs text-gray-500 mt-1">Cadastrar cliente</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="group cursor-pointer border shadow-sm bg-gradient-to-br from-purple-50 to-white transition-all duration-300 transform hover:-translate-y-1"
                  onClick={() => navigate('/customers')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                        <Users className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-gray-900">Gerenciar Clientes</h3>
                        <p className="text-xs text-gray-500 mt-1">Ver todos os clientes</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="group cursor-pointer border shadow-sm bg-gradient-to-br from-orange-50 to-white transition-all duration-300 transform hover:-translate-y-1"
                  onClick={() => navigate('/dashboard')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-3 bg-orange-100 rounded-xl group-hover:bg-orange-200 transition-colors">
                        <BarChart3 className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-gray-900">Dashboard Completo</h3>
                        <p className="text-xs text-gray-500 mt-1">Ver todas as métricas</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Bottom Spacing */}
          <div className="h-16" />
        </div>
      </div>
    </>
  );
};

export default HomePage;
